import Dashboard from "@/pages/DashboardFinanceur";

async function saveRate(data) {
  const token = localStorage.getItem("jwtToken");

  try {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(
      `${import.meta.env.VITE_IP_API}/api/exchange_rate/save`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      }
    );

    return res;
  } catch (error) {}
}

type TodayRateApiResult = {
  rateToEuro: number | null;
  ratedAt: string | null;
};

const todayRateCache = new Map<string, Promise<TodayRateApiResult>>();

function formatDateForRateApi(dateInput: Date): string {
  const year = dateInput.getFullYear();
  const month = String(dateInput.getMonth() + 1).padStart(2, "0");
  const day = String(dateInput.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function fetchTodayRateToEuroFromHexa(
  deviseInput: any,
): Promise<TodayRateApiResult> {
  const code = String(deviseInput?.code ?? "")
    .trim()
    .toUpperCase();
  if (!code || code === "EUR") {
    return { rateToEuro: code === "EUR" ? 1 : null, ratedAt: null };
  }

  const today = formatDateForRateApi(new Date());
  const cacheKey = `${code}:${today}`;
  const cached = todayRateCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async (): Promise<TodayRateApiResult> => {
    try {
      const res = await fetch(
        `https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${code}&date=${today}`,
      );
      if (!res.ok) return { rateToEuro: null, ratedAt: today };

      const data = await res.json();
      const entries = Array.isArray(data) ? data : [];
      const entry = (entries as any[]).find((e) => e.quote === code);
      const rate = Number(entry?.rate);
      if (!Number.isFinite(rate) || rate <= 0) {
        return { rateToEuro: null, ratedAt: today };
      }

      return { rateToEuro: 1 / rate, ratedAt: today };
    } catch {
      return { rateToEuro: null, ratedAt: today };
    }
  })();

  todayRateCache.set(cacheKey, promise);
  return promise;
}

export async function checkRate(devise) {
  const devises = localStorage.getItem("devises");
  if (devises) {
    const devisesJSON = JSON.parse(devises);
    devise = devisesJSON.filter((d: any) => d.code === devise.code)[0];
  }

  const apiRate = await fetchTodayRateToEuroFromHexa(devise);
  if (
    typeof apiRate.rateToEuro === "number" &&
    Number.isFinite(apiRate.rateToEuro) &&
    apiRate.rateToEuro > 0
  ) {
    return apiRate.rateToEuro;
  }

  if (
    devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;

    return rate;
  }
}

export async function convertToEuro(
  amount: number,
  devise: any,
  ownRate?: any
) {
  if (devise === undefined || devise === null || devise?.code === "EUR") {
    return amount;
  }
  const devises = localStorage.getItem("devises");
  if (devises) {
    const devisesJSON = JSON.parse(devises);
    devise = devisesJSON.filter((d: any) => d.code === devise?.code)[0];
  }

  const ownRateNumber = Number(ownRate);
  if (Number.isFinite(ownRateNumber) && ownRateNumber > 0) {
    return amount * ownRateNumber;
  }
  const apiRateToEuro = (await fetchTodayRateToEuroFromHexa(devise)).rateToEuro;
  if (typeof apiRateToEuro === "number" && apiRateToEuro > 0) {
    return amount * apiRateToEuro;
  }

  if (
    devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;

    return amount * rate;
  } else {
    const response = {
      meta: {
        last_updated_at: "2024-09-18T23:59:59Z",
      },
      data: {
        EUR: {
          code: "EUR",
          value: 1,
        },
      },
    };

    // if (import.meta.env.VITE_ACTIVE_AMOUNTCONVERTER == 1) {
    //   const currencyApi = new CurrencyAPI(import.meta.env.VITE_CURRENCYAPI_API_KEY);

    //   response = await currencyApi.latest({
    //     base_currency: devise.code,
    //     currencies: "EUR",
    //   });
    // }

    if (
      response.data !== undefined &&
      response.data.EUR !== undefined &&
      response.data.EUR.value !== undefined
    ) {
      amount = amount * response.data.EUR.value;
    }

    return amount;
  }
}
export async function convertEuroToCurrency(
  amount: number,
  devise: any,
  ownRate?: any,
  dashboard = false
) {
  if (devise === undefined || devise === null || devise?.code === "EUR")
    return amount;
  const devises = localStorage.getItem("devises");
  if (devises && !dashboard) {
    const devisesJSON = JSON.parse(devises);
    devise = devisesJSON.filter((d: any) => d.code === devise.code)[0];
  }

  const ownRateNumber = Number(ownRate);
  if (Number.isFinite(ownRateNumber) && ownRateNumber > 0) {
    return amount / ownRateNumber;
  }
  const apiRateToEuro = (await fetchTodayRateToEuroFromHexa(devise)).rateToEuro;
  if (typeof apiRateToEuro === "number" && apiRateToEuro > 0) {
    return amount / apiRateToEuro;
  }
  if (
    devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;
    return amount / rate;
  } else {
    const response = {
      meta: {
        last_updated_at: "2024-09-18T23:59:59Z",
      },
      data: {
        EUR: {
          code: "EUR",
          value: 1,
        },
      },
    };

    // if (import.meta.env.VITE_ACTIVE_AMOUNTCONVERTER == 1) {
    //   const currencyApi = new CurrencyAPI(import.meta.env.VITE_CURRENCYAPI_API_KEY);

    //   response = await currencyApi.latest({
    //     base_currency: devise.code,
    //     currencies: "EUR",
    //   });
    // }

    if (
      response.data !== undefined &&
      response.data.EUR !== undefined &&
      response.data.EUR.value !== undefined
    ) {
      amount = amount / response.data.EUR.value;
    }

    return amount;
  }
}
export async function convertEuroToCurrencyV2(
  amount: number,
  devise: any,
  ownRate?: any
) {
  if (devise === undefined || devise === null || devise?.code === "EUR")
    return amount;
  const ownRateNumber = Number(ownRate);
  if (Number.isFinite(ownRateNumber) && ownRateNumber > 0) {
    return amount / ownRateNumber;
  }
  const apiRateToEuro = (await fetchTodayRateToEuroFromHexa(devise)).rateToEuro;
  if (typeof apiRateToEuro === "number" && apiRateToEuro > 0) {
    return amount / apiRateToEuro;
  }
  if (
    devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    ).length > 0
  ) {
    const rate = devise?.exchangeRates.filter(
      (exchangeRate) => exchangeRate.targetCurrency.code === "EUR"
    )[0].rate;
    return amount / rate;
  } else {
    const response = {
      meta: {
        last_updated_at: "2024-09-18T23:59:59Z",
      },
      data: {
        EUR: {
          code: "EUR",
          value: 1,
        },
      },
    };

    // if (import.meta.env.VITE_ACTIVE_AMOUNTCONVERTER == 1) {
    //   const currencyApi = new CurrencyAPI(import.meta.env.VITE_CURRENCYAPI_API_KEY);

    //   response = await currencyApi.latest({
    //     base_currency: devise.code,
    //     currencies: "EUR",
    //   });
    // }

    if (
      response.data !== undefined &&
      response.data.EUR !== undefined &&
      response.data.EUR.value !== undefined
    ) {
      amount = amount / response.data.EUR.value;
    }

    return amount;
  }
}
